import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Code2, Hash, ListChecks, TerminalSquare, Search } from 'lucide-react';
import Inline from '../components/Inline';

const SHEETS = [
  {
    id: 'pv',
    label: 'Pseudo-variables',
    icon: Code2,
    head: ['Variable', 'Meaning'],
    rows: [
      ['`$rm`', 'Request method'],
      ['`$ru` / `$rU` / `$rd`', 'Request-URI / user / domain'],
      ['`$du`', 'Destination URI (next hop)'],
      ['`$fu` / `$fU` / `$fd`', 'From URI / user / domain'],
      ['`$tu` / `$tU` / `$td`', 'To URI / user / domain'],
      ['`$ft` / `$tt`', 'From tag / To tag'],
      ['`$ci`', 'Call-ID'],
      ['`$cs`', 'CSeq number'],
      ['`$ua`', 'User-Agent'],
      ['`$hdr(Name)`', 'Header body'],
      ['`$rb`', 'Message body'],
      ['`$rs` / `$rr`', 'Reply code / reason'],
      ['`$si` / `$sp`', 'Source IP / port'],
      ['`$Ri` / `$Rp`', 'Local receiving IP / port'],
      ['`$pr`', 'Protocol (udp, tcp, tls, ws, wss)'],
      ['`$au`', 'Auth username'],
      ['`$T_reply_code`', 'Winning reply code in failure_route'],
      ['`$var(x)`', 'Private script variable'],
      ['`$avp(x)`', 'Transaction-scoped variable'],
      ['`$shv(x)`', 'Shared global variable'],
      ['`$sht(t=>k)`', 'htable entry'],
      ['`$rc`', 'Return code of the last action'],
    ],
  },
  {
    id: 'fn',
    label: 'Functions',
    icon: ListChecks,
    head: ['Function', 'Module', 'What it does'],
    rows: [
      ['`sl_send_reply(code, reason)`', 'sl', 'Stateless reply'],
      ['`t_relay()`', 'tm', 'Forward statefully'],
      ['`t_newtran()` + `t_reply()`', 'tm', 'Stateful local reply'],
      ['`t_on_failure("NAME")`', 'tm', 'Arm a failure_route'],
      ['`t_on_reply("NAME")`', 'tm', 'Arm an onreply_route'],
      ['`t_check_status("486|408")`', 'tm', 'Match the reply code by regex'],
      ['`t_is_canceled()`', 'tm', 'Caller cancelled the transaction'],
      ['`record_route()`', 'rr', 'Stay in the dialog path'],
      ['`loose_route()`', 'rr', 'Route by Route headers'],
      ['`has_totag()`', 'siputils', 'Request is inside a dialog'],
      ['`is_method("INVITE|BYE")`', 'textops', 'Match the method'],
      ['`mf_process_maxfwd_header("10")`', 'maxfwd', 'Loop protection'],
      ['`sanity_check()`', 'sanity', 'Reject malformed messages'],
      ['`save("location")`', 'registrar', 'Store REGISTER bindings'],
      ['`lookup("location")`', 'registrar', 'Find a user\'s contacts'],
      ['`auth_check(realm, table, flags)`', 'auth_db', 'Verify digest credentials'],
      ['`auth_challenge(realm, flags)`', 'auth', 'Send 401 or 407'],
      ['`pike_check_req()`', 'pike', 'Flood detection'],
      ['`allow_source_address(group)`', 'permissions', 'Trust by source IP'],
      ['`ds_select_dst(set, alg)`', 'dispatcher', 'Pick a destination'],
      ['`ds_next_dst()`', 'dispatcher', 'Fail over to the next one'],
      ['`nat_uac_test(flags)`', 'nathelper', 'Detect NAT'],
      ['`fix_nated_register()`', 'nathelper', 'Save the real source on REGISTER'],
      ['`rtpengine_manage(flags)`', 'rtpengine', 'Anchor media'],
      ['`xlog(level, text)`', 'xlog', 'Log with variables'],
    ],
  },
  {
    id: 'cli',
    label: 'Commands',
    icon: TerminalSquare,
    head: ['Command', 'What it does'],
    rows: [
      ['`kamailio -c -f kamailio.cfg`', 'Check config syntax'],
      ['`kamailio -DD -E -f kamailio.cfg`', 'Run in foreground, log to terminal'],
      ['`kamailio -m 256 -M 16`', 'Shared / private memory in MB'],
      ['`kamdbctl create`', 'Create the database'],
      ['`kamctl add user@domain pass`', 'Add a subscriber'],
      ['`kamctl ul show`', 'Show registrations'],
      ['`kamctl address add 1 10.0.0.20 32 0 tag`', 'Trust an IP'],
      ['`kamcmd core.uptime`', 'Uptime'],
      ['`kamcmd core.psx`', 'List processes'],
      ['`kamcmd ul.dump`', 'Dump location table'],
      ['`kamcmd stats.get_statistics all`', 'All statistics'],
      ['`kamcmd cfg.seti core debug 3`', 'Raise log level live'],
      ['`kamcmd dispatcher.list`', 'Dispatcher state'],
      ['`kamcmd dispatcher.reload`', 'Reload dispatcher list'],
      ['`kamcmd htable.dump ipban`', 'Show banned IPs'],
      ['`kamcmd permissions.addressReload`', 'Reload trusted IPs'],
      ['`kamcmd tls.reload`', 'Reload certificates'],
      ['`kamcmd system.listMethods`', 'List RPC commands'],
      ['`sngrep -d any port 5060`', 'Live SIP ladder'],
    ],
  },
  {
    id: 'codes',
    label: 'Response codes',
    icon: Hash,
    head: ['Code', 'Reason', 'Class'],
    rows: [
      ['100', 'Trying', 'Provisional'],
      ['180', 'Ringing', 'Provisional'],
      ['183', 'Session Progress', 'Provisional'],
      ['200', 'OK', 'Success'],
      ['202', 'Accepted', 'Success'],
      ['301', 'Moved Permanently', 'Redirect'],
      ['302', 'Moved Temporarily', 'Redirect'],
      ['401', 'Unauthorized', 'Client error'],
      ['403', 'Forbidden', 'Client error'],
      ['404', 'Not Found', 'Client error'],
      ['405', 'Method Not Allowed', 'Client error'],
      ['407', 'Proxy Authentication Required', 'Client error'],
      ['408', 'Request Timeout', 'Client error'],
      ['480', 'Temporarily Unavailable', 'Client error'],
      ['481', 'Call/Transaction Does Not Exist', 'Client error'],
      ['483', 'Too Many Hops', 'Client error'],
      ['484', 'Address Incomplete', 'Client error'],
      ['486', 'Busy Here', 'Client error'],
      ['487', 'Request Terminated', 'Client error'],
      ['488', 'Not Acceptable Here', 'Client error'],
      ['500', 'Server Internal Error', 'Server error'],
      ['503', 'Service Unavailable', 'Server error'],
      ['504', 'Server Time-out', 'Server error'],
      ['600', 'Busy Everywhere', 'Global failure'],
      ['603', 'Decline', 'Global failure'],
    ],
  },
];

export default function CheatSheet() {
  const [tab, setTab] = useState('pv');
  const [filter, setFilter] = useState('');
  const sheet = SHEETS.find((s) => s.id === tab);
  const rows = sheet.rows.filter((r) => r.join(' ').toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="page cheatsheet">
      <div className="section-head left">
        <p className="eyebrow">Reference</p>
        <h1 className="page-title">Cheat sheet</h1>
        <p>The things you look up every day while writing kamailio.cfg.</p>
      </div>
      <div className="tabs" role="tablist">
        {SHEETS.map((s) => {
          const I = s.icon;
          return (
            <button key={s.id} role="tab" type="button" aria-selected={tab === s.id} className={`tab ${tab === s.id ? 'is-active' : ''}`} onClick={() => setTab(s.id)}>
              {tab === s.id && <motion.span layoutId="tab-bg" className="tab-bg" />}
              <I size={16} /> <span>{s.label}</span>
            </button>
          );
        })}
      </div>
      <label className="filter" htmlFor="cheat-filter">
        <Search size={16} />
        <input id="cheat-filter" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={`Filter ${sheet.label.toLowerCase()}`} />
      </label>
      <AnimatePresence mode="wait">
        <motion.div key={tab} className="table-wrap" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
          <table className={`cheat-table cheat-${tab}`}>
            <thead><tr>{sheet.head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>{r.map((c, j) => <td key={j} data-class={tab === 'codes' && j === 0 ? c[0] : undefined}><Inline text={c} /></td>)}</tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={sheet.head.length}>Nothing matches “{filter}”.</td></tr>}
            </tbody>
          </table>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
